import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import ChatWidget from './components/ChatWidget';
import './App.css';

// Eagerly loaded (critical path)
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import { apiFetch } from './utils/api';

// Lazy loaded pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const EntryManagement = React.lazy(() => import('./pages/EntryManagement'));
const ExitManagement = React.lazy(() => import('./pages/ExitManagement'));
const TicketManagement = React.lazy(() => import('./pages/TicketManagement'));
const CustomerManagement = React.lazy(() => import('./pages/CustomerManagement'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Reports = React.lazy(() => import('./pages/Reports'));
const LegacyPaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const SmartHome = React.lazy(() => import('./pages/SmartHome'));
const TaxReport = React.lazy(() => import('./pages/TaxReport'));
const RentalManagement = React.lazy(() => import('./pages/RentalManagement'));
const AdvancedStatistics = React.lazy(() => import('./pages/AdvancedStatistics'));
const OccupancyMap = React.lazy(() => import('./pages/OccupancyMap'));
const Reservations = React.lazy(() => import('./pages/Reservations'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const CashRegister = React.lazy(() => import('./pages/CashRegister'));
const StaffManagement = React.lazy(() => import('./pages/StaffManagement'));
const ExpensesAdvanced = React.lazy(() => import('./pages/ExpensesAdvanced'));
const AuditLogs = React.lazy(() => import('./pages/AuditLogs'));
const EVCharging = React.lazy(() => import('./pages/EVCharging'));
const Payroll = React.lazy(() => import('./pages/Payroll'));
const Insurance = React.lazy(() => import('./pages/Insurance'));
const Scheduling = React.lazy(() => import('./pages/Scheduling'));
const Ergani = React.lazy(() => import('./pages/Ergani'));
const Equipment = React.lazy(() => import('./pages/Equipment'));
const GroundSensors = React.lazy(() => import('./pages/GroundSensors'));
const FireSafety = React.lazy(() => import('./pages/FireSafety'));
const Micromobility = React.lazy(() => import('./pages/Micromobility'));
const RentalReceipt = React.lazy(() => import('./pages/RentalReceipt'));
const AlarmSystem = React.lazy(() => import('./pages/AlarmSystem'));
const ElectricalAutomations = React.lazy(() => import('./pages/ElectricalAutomations'));
const AccountantFolder = React.lazy(() => import('./pages/AccountantFolder'));
const PricingView = React.lazy(() => import('./pages/PricingView'));
const POSEpsilonSettings = React.lazy(() => import('./pages/POSEpsilonSettings'));
const ZReport = React.lazy(() => import('./pages/ZReport'));
const Terminal = React.lazy(() => import('./pages/Terminal'));
const PartnerBusinesses = React.lazy(() => import('./pages/PartnerBusinesses'));
const ShiftHandover = React.lazy(() => import('./pages/ShiftHandover'));
const DailyChecklist = React.lazy(() => import('./pages/DailyChecklist'));
const IncidentLog = React.lazy(() => import('./pages/IncidentLog'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const Reminders = React.lazy(() => import('./pages/Reminders'));
const ReoLinkCameras = React.lazy(() => import('./pages/ReoLinkCameras'));
const PlateRecognition = React.lazy(() => import('./pages/PlateRecognition'));
const PublicLegalPage = React.lazy(() => import('./pages/PublicLegalPage'));
const PublicParkingPage = React.lazy(() => import('./pages/PublicParkingPage'));
const AIReviewer = React.lazy(() => import('./pages/AIReviewer'));
const Loyalty = React.lazy(() => import('./pages/Loyalty'));
// Phase 6.14 (v1.0.9.47) — user-facing loyalty lookup window (non-admin accessible)
const LoyaltyLookup = React.lazy(() => import('./pages/LoyaltyLookup'));

// v1.0.9.33 · Public payment landing pages + legal/info (Viva source checklist)
const PaymentSuccess = React.lazy(() => import('./pages/public/PaymentSuccess'));
const PaymentFailure = React.lazy(() => import('./pages/public/PaymentFailure'));
const PublicPrivacy  = React.lazy(() => import('./pages/public/Privacy'));
const PublicTerms    = React.lazy(() => import('./pages/public/Terms'));
const PublicContact  = React.lazy(() => import('./pages/public/Contact'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Φόρτωση...</p>
      </div>
    </div>
  );
}

// Super Admin route protection — verifies via backend, not localStorage
function SuperAdminRoute({ children }) {
  const [verified, setVerified] = React.useState(null);
  React.useEffect(() => {
    apiFetch(`/api/super-admin/me`)
      .then(r => { if (r.ok) setVerified(true); else setVerified(false); })
      .catch(() => setVerified(false));
  }, []);
  if (verified === null) return <PageLoader />;
  if (!verified) return <Navigate to="/login" replace />;
  return children;
}

function SuperAdminEntry() {
  const [verified, setVerified] = React.useState(null);
  React.useEffect(() => {
    apiFetch(`/api/super-admin/me`)
      .then(r => { if (r.ok) setVerified(true); else setVerified(false); })
      .catch(() => setVerified(false));
  }, []);
  if (verified === null) return <PageLoader />;
  if (!verified) return <Login forceLoginMode="super_admin" />;
  return <SuperAdminDashboard />;
}

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(() => console.log('SW registered'))
      .catch((err) => console.log('SW registration failed:', err));
  });
}

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render (not in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <React.Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/super-admin" element={<SuperAdminEntry />} />

          {/* v1.0.9.33 · Public routes (no auth) — Viva payment landings + legal pages */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/failure" element={<PaymentFailure />} />
          <Route path="/privacy" element={<PublicPrivacy />} />
          <Route path="/terms"   element={<PublicTerms />} />
          <Route path="/contact" element={<PublicContact />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/entry" element={<ProtectedRoute><EntryManagement /></ProtectedRoute>} />
          <Route path="/exit" element={<ProtectedRoute><ExitManagement /></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute><TicketManagement /></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute><CustomerManagement /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
          <Route path="/rentals" element={<ProtectedRoute><RentalManagement /></ProtectedRoute>} />
          <Route path="/tax-report" element={<ProtectedRoute><TaxReport /></ProtectedRoute>} />
          <Route path="/smart-home" element={<ProtectedRoute><SmartHome /></ProtectedRoute>} />
          <Route path="/statistics" element={<ProtectedRoute><AdvancedStatistics /></ProtectedRoute>} />
          <Route path="/occupancy" element={<ProtectedRoute><OccupancyMap /></ProtectedRoute>} />
          <Route path="/reservations" element={<ProtectedRoute><Reservations /></ProtectedRoute>} />
          <Route path="/cash-register" element={<ProtectedRoute><CashRegister /></ProtectedRoute>} />
          <Route path="/staff" element={<ProtectedRoute><StaffManagement /></ProtectedRoute>} />
          <Route path="/scheduling" element={<ProtectedRoute><Scheduling /></ProtectedRoute>} />
          <Route path="/ergani" element={<ProtectedRoute><Ergani /></ProtectedRoute>} />
          <Route path="/expenses-advanced" element={<ProtectedRoute><ExpensesAdvanced /></ProtectedRoute>} />
          <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
          <Route path="/ev-charging" element={<ProtectedRoute><EVCharging /></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
          <Route path="/insurance" element={<ProtectedRoute><Insurance /></ProtectedRoute>} />
          <Route path="/equipment" element={<ProtectedRoute><Equipment /></ProtectedRoute>} />
          <Route path="/ground-sensors" element={<ProtectedRoute><GroundSensors /></ProtectedRoute>} />
          <Route path="/fire-safety" element={<ProtectedRoute><FireSafety /></ProtectedRoute>} />
          <Route path="/micromobility" element={<ProtectedRoute><Micromobility /></ProtectedRoute>} />
          <Route path="/rental-receipt/:rentalId" element={<RentalReceipt />} />
          <Route path="/alarm-system" element={<ProtectedRoute><AlarmSystem /></ProtectedRoute>} />
          <Route path="/electrical" element={<ProtectedRoute><ElectricalAutomations /></ProtectedRoute>} />
          <Route path="/accountant-folder" element={<ProtectedRoute><AccountantFolder /></ProtectedRoute>} />
          <Route path="/pricing-view" element={<ProtectedRoute><PricingView /></ProtectedRoute>} />
          <Route path="/pos-epsilon" element={<ProtectedRoute><POSEpsilonSettings /></ProtectedRoute>} />
          <Route path="/settings/pos-epsilon" element={<ProtectedRoute><POSEpsilonSettings /></ProtectedRoute>} />
          <Route path="/z-report" element={<ProtectedRoute><ZReport /></ProtectedRoute>} />
          <Route path="/terminal" element={<ProtectedRoute><Terminal /></ProtectedRoute>} />
          <Route path="/partner-businesses" element={<ProtectedRoute><PartnerBusinesses /></ProtectedRoute>} />
          <Route path="/shift-handover" element={<ProtectedRoute><ShiftHandover /></ProtectedRoute>} />
          <Route path="/checklist" element={<ProtectedRoute><DailyChecklist /></ProtectedRoute>} />
          <Route path="/incidents" element={<ProtectedRoute><IncidentLog /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
          <Route path="/reolink" element={<ProtectedRoute><ReoLinkCameras /></ProtectedRoute>} />
          <Route path="/plate-recognition" element={<ProtectedRoute><PlateRecognition /></ProtectedRoute>} />
          <Route path="/ai-reviewer" element={<ProtectedRoute><AIReviewer /></ProtectedRoute>} />
          <Route path="/payment-success" element={<ProtectedRoute><LegacyPaymentSuccess /></ProtectedRoute>} />
          <Route path="/loyalty" element={<ProtectedRoute><Loyalty /></ProtectedRoute>} />
          {/* Phase 6.14 (v1.0.9.47) — user-facing loyalty lookup (non-admin accessible) */}
          <Route path="/loyalty-lookup" element={<ProtectedRoute><LoyaltyLookup /></ProtectedRoute>} />
          <Route path="/legal/:type" element={<PublicLegalPage />} />
          <Route path="/parking" element={<PublicParkingPage />} />
          <Route path="/p/:publicCode" element={<PublicParkingPage />} />
          <Route path="/p/:publicCode/legal/:type" element={<PublicLegalPage />} />
        </Routes>
      </React.Suspense>
      <Toaster position="top-center" richColors />
      <ChatWidget />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}
